import React from "react";
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Button,
  Text,
  Card,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { ResponsiveLine } from "@nivo/line";
import { PaymentEntry } from "../types/api";

const useStyles = makeStyles({
  dialogContent: {
    width: "80vw",
    maxWidth: "1200px",
    height: "80vh",
    maxHeight: "800px",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  accordion: {
    flex: 1,
    minHeight: 0,
  },
  accordionPanel: {
    height: "450px", // Fixed height for accordion panels
    display: "flex",
    flexDirection: "column",
  },
  salesInfoCard: {
    marginBottom: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  chartsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingVerticalL,
    flex: 1,
    minHeight: 0, // Allow grid to shrink
  },
  chartCard: {
    padding: tokens.spacingVerticalM,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  chartTitle: {
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  chartTitle2: {
    marginBottom: 0,
    paddingBottom: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  chartSubtitle: {
    color: tokens.colorNeutralForeground4,
  },
  chartContainer: {
    height: "400px", // Fixed height for charts inside accordion
    minHeight: "400px",
    width: "100%",
    flex: 1,
  },
  skuInfo: {
    marginBottom: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: tokens.spacingVerticalM,
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  value: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
  },
});

interface SkuChartsModalProps {
  isOpen: boolean;
  onClose: () => void;
  skuData: {
    sku: string;
    itemName: string;
    cfroi: number;
    investment: number;
    currentStock: number;
    npv: number;
    paymentSeries: {
      b2cEntries: PaymentEntry[];
      b2bEntries: PaymentEntry[] | null;
      totalRevenue: number;
      b2cTotalRevenue: number;
      b2bTotalRevenue: number;
    };
  } | null;
}

const SkuChartsModal: React.FC<SkuChartsModalProps> = ({
  isOpen,
  onClose,
  skuData,
}) => {
  const styles = useStyles();

  if (!skuData) return null;

  // Prepare data for the combined accumulated revenue chart
  let combinedAccumulated = 0;
  const allPeriods = new Set<number>();

  // Collect all periods from both B2C and B2B
  if (skuData.paymentSeries.b2cEntries) {
    skuData.paymentSeries.b2cEntries.forEach((entry) =>
      allPeriods.add(entry.period)
    );
  }
  if (skuData.paymentSeries.b2bEntries) {
    skuData.paymentSeries.b2bEntries.forEach((entry) =>
      allPeriods.add(entry.period)
    );
  }

  // Sort periods and calculate combined accumulated revenue
  const sortedPeriods = Array.from(allPeriods).sort((a, b) => a - b);
  const combinedData = [
    {
      id: "Total Accumulated Cashflow",
      data: sortedPeriods.map((period) => {
        // Get revenue for this period from both B2C and B2B
        const b2cEntry = skuData.paymentSeries.b2cEntries?.find(
          (entry) => entry.period === period
        );
        const b2bEntry = skuData.paymentSeries.b2bEntries?.find(
          (entry) => entry.period === period
        );

        const periodRevenue =
          (b2cEntry?.revenue || 0) + (b2bEntry?.revenue || 0);
        combinedAccumulated += periodRevenue;

        return {
          x: period,
          y: combinedAccumulated,
        };
      }),
    },
  ];

  // Prepare data for the accumulated revenue chart
  let accumulatedB2C = 0;
  let accumulatedB2B = 0;

  const accumulatedData = [
    {
      id: "Accumulated B2C",
      data:
        skuData.paymentSeries.b2cEntries?.map((entry) => {
          accumulatedB2C += entry.revenue;
          return {
            x: entry.period,
            y: accumulatedB2C,
          };
        }) || [],
    },
  ];

  if (
    skuData.paymentSeries.b2bEntries &&
    skuData.paymentSeries.b2bEntries.length > 0
  ) {
    accumulatedData.push({
      id: "Accumulated B2B",
      data: skuData.paymentSeries.b2bEntries.map((entry) => {
        accumulatedB2B += entry.revenue;
        return {
          x: entry.period,
          y: accumulatedB2B,
        };
      }),
    });
  }

  // Calculate total quantities sold
  const totalB2CQuantity =
    skuData.paymentSeries.b2cEntries?.reduce(
      (sum, entry) => sum + (entry.soldQuantity || 0),
      0
    ) || 0;
  const totalB2BQuantity =
    skuData.paymentSeries.b2bEntries?.reduce(
      (sum, entry) => sum + (entry.soldQuantity || 0),
      0
    ) || 0;

  // Prepare daily sales data for chart
  const dailySalesData = [];
  if (skuData.paymentSeries.b2cEntries?.length) {
    dailySalesData.push({
      id: "B2C Sales",
      data: skuData.paymentSeries.b2cEntries.map((entry) => ({
        x: entry.period,
        y: entry.soldQuantity || 0,
      })),
    });
  }
  if (skuData.paymentSeries.b2bEntries?.length) {
    dailySalesData.push({
      id: "B2B Sales",
      data: skuData.paymentSeries.b2bEntries.map((entry) => ({
        x: entry.period,
        y: entry.soldQuantity || 0,
      })),
    });
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const chartTheme = {
    background: "transparent",
    text: {
      fontSize: 12,
      fill: tokens.colorNeutralForeground1,
    },
    axis: {
      domain: {
        line: {
          stroke: tokens.colorNeutralStroke1,
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: 12,
          fill: tokens.colorNeutralForeground1,
        },
      },
      ticks: {
        line: {
          stroke: tokens.colorNeutralStroke1,
          strokeWidth: 1,
        },
        text: {
          fontSize: 11,
          fill: tokens.colorNeutralForeground2,
        },
      },
    },
    grid: {
      line: {
        stroke: tokens.colorNeutralStroke2,
        strokeWidth: 1,
      },
    },
    legends: {
      text: {
        fontSize: 12,
        fill: tokens.colorNeutralForeground1,
      },
    },
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogContent}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle>Cashflow Analysis - {skuData.itemName}</DialogTitle>
          <DialogContent
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            {/* SKU Information */}
            <div className={styles.skuInfo}>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>CFROI</Text>
                  <Text className={styles.value}>
                    {(skuData.cfroi * 100).toFixed(2)}%
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>Investment</Text>
                  <Text className={styles.value}>
                    {formatCurrency(skuData.investment)}
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>NPV</Text>
                  <Text className={styles.value}>
                    {formatCurrency(skuData.npv)}
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>Current Stock</Text>
                  <Text className={styles.value}>{skuData.currentStock}</Text>
                </div>
              </div>
            </div>

            {/* Sales Summary Card */}
            <Card className={styles.salesInfoCard}>
              <Text className={styles.chartTitle}>Sales Summary</Text>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>Total Revenue</Text>
                  <Text className={styles.value}>
                    {formatCurrency(skuData.paymentSeries.totalRevenue)}
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>B2C Revenue</Text>
                  <Text className={styles.value}>
                    {formatCurrency(skuData.paymentSeries.b2cTotalRevenue)}
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>B2B Revenue</Text>
                  <Text className={styles.value}>
                    {formatCurrency(skuData.paymentSeries.b2bTotalRevenue)}
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>Total Units Sold</Text>
                  <Text className={styles.value}>
                    {totalB2CQuantity + totalB2BQuantity} units
                  </Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>B2C Units</Text>
                  <Text className={styles.value}>{totalB2CQuantity} units</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.label}>B2B Units</Text>
                  <Text className={styles.value}>{totalB2BQuantity} units</Text>
                </div>
              </div>
            </Card>

            {/* Charts */}
            <Accordion
              className={styles.accordion}
              defaultOpenItems="0"
              collapsible
            >
              <AccordionItem value="0">
                <AccordionHeader>Total Accumulated Cashflow</AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <Text size={200} className={styles.chartSubtitle}>
                    Please note the total investment value is not discounted and
                    is shown as a reference line.
                  </Text>
                  <div className={styles.chartContainer}>
                    <ResponsiveLine
                      data={combinedData}
                      theme={chartTheme}
                      margin={{ top: 20, right: 20, bottom: 60, left: 80 }}
                      xScale={{ type: "linear", min: "auto", max: "auto" }}
                      yScale={{ type: "linear", min: 0, max: "auto" }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Days",
                        legendOffset: 36,
                        legendPosition: "middle",
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Total Cashflow (€)",
                        legendOffset: -60,
                        legendPosition: "middle",
                        format: (value) => formatCurrency(value),
                      }}
                      markers={[
                        {
                          axis: "y",
                          value: skuData.investment,
                          lineStyle: {
                            stroke: tokens.colorPaletteRedBackground3,
                            strokeWidth: 2,
                            strokeDasharray: "5,5",
                          },
                          legend: `Investment Level (${formatCurrency(
                            skuData.investment
                          )})`,
                          legendOrientation: "horizontal",
                          legendPosition: "bottom-left",
                          textStyle: {
                            fill: tokens.colorPaletteRedForeground1,
                            fontSize: 12,
                            fontWeight: 600,
                          },
                        },
                      ]}
                      pointSize={6}
                      pointColor={{ theme: "background" }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      enableGridX={true}
                      enableGridY={true}
                      colors={[tokens.colorPaletteBlueBorderActive]}
                      legends={[
                        {
                          anchor: "top-left",
                          direction: "column",
                          justify: false,
                          translateX: 5,
                          translateY: 5,
                          itemsSpacing: 0,
                          itemDirection: "left-to-right",
                          itemWidth: 80,
                          itemHeight: 20,
                          itemOpacity: 0.75,
                          symbolSize: 12,
                          symbolShape: "circle",
                          symbolBorderColor: "rgba(0, 0, 0, .5)",
                        },
                      ]}
                      tooltip={({ point }) => (
                        <div
                          style={{
                            background: tokens.colorNeutralBackground1,
                            padding: "9px 12px",
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            borderRadius: tokens.borderRadiusSmall,
                          }}
                        >
                          <strong>{point.seriesId}</strong>
                          <br />
                          Day: {String(point.data.x)}
                          <br />
                          Total Cashflow: {formatCurrency(Number(point.data.y))}
                        </div>
                      )}
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem value="1">
                <AccordionHeader>
                  Accumulated Cashflow by Channel
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <div className={styles.chartContainer}>
                    <ResponsiveLine
                      data={accumulatedData}
                      theme={chartTheme}
                      margin={{ top: 20, right: 20, bottom: 60, left: 80 }}
                      xScale={{ type: "linear", min: "auto", max: "auto" }}
                      yScale={{ type: "linear", min: 0, max: "auto" }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Days",
                        legendOffset: 36,
                        legendPosition: "middle",
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Accumulated Cashflow (€)",
                        legendOffset: -60,
                        legendPosition: "middle",
                        format: (value) => formatCurrency(value),
                      }}
                      pointSize={6}
                      pointColor={{ theme: "background" }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      enableGridX={true}
                      enableGridY={true}
                      colors={[
                        tokens.colorPaletteBlueBorderActive,
                        tokens.colorPaletteGreenBorderActive,
                      ]}
                      legends={[
                        {
                          anchor: "top-left",
                          direction: "column",
                          justify: false,
                          translateX: 5,
                          translateY: 5,
                          itemsSpacing: 0,
                          itemDirection: "left-to-right",
                          itemWidth: 80,
                          itemHeight: 20,
                          itemOpacity: 0.75,
                          symbolSize: 12,
                          symbolShape: "circle",
                          symbolBorderColor: "rgba(0, 0, 0, .5)",
                        },
                      ]}
                      tooltip={({ point }) => (
                        <div
                          style={{
                            background: tokens.colorNeutralBackground1,
                            padding: "9px 12px",
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            borderRadius: tokens.borderRadiusSmall,
                          }}
                        >
                          <strong>{point.seriesId}</strong>
                          <br />
                          Day: {String(point.data.x)}
                          <br />
                          Accumulated: {formatCurrency(Number(point.data.y))}
                        </div>
                      )}
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem value="2">
                <AccordionHeader>Daily Sales Quantities</AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <div className={styles.chartContainer}>
                    <ResponsiveLine
                      data={dailySalesData}
                      theme={chartTheme}
                      margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                      xScale={{ type: "linear", min: "auto", max: "auto" }}
                      yScale={{ type: "linear", min: 0, max: "auto" }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Days",
                        legendOffset: 36,
                        legendPosition: "middle",
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Units Sold",
                        legendOffset: -45,
                        legendPosition: "middle",
                      }}
                      pointSize={6}
                      pointColor={{ theme: "background" }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      enableGridX={true}
                      enableGridY={true}
                      colors={[
                        tokens.colorPaletteBlueBorderActive,
                        tokens.colorPaletteGreenBorderActive,
                      ]}
                      legends={[
                        {
                          anchor: "top-left",
                          direction: "column",
                          justify: false,
                          translateX: 5,
                          translateY: 5,
                          itemsSpacing: 0,
                          itemDirection: "left-to-right",
                          itemWidth: 80,
                          itemHeight: 20,
                          itemOpacity: 0.75,
                          symbolSize: 12,
                          symbolShape: "circle",
                          symbolBorderColor: "rgba(0, 0, 0, .5)",
                        },
                      ]}
                      tooltip={({ point }) => (
                        <div
                          style={{
                            background: tokens.colorNeutralBackground1,
                            padding: "9px 12px",
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            borderRadius: tokens.borderRadiusSmall,
                          }}
                        >
                          <strong>{point.seriesId}</strong>
                          <br />
                          Day: {String(point.data.x)}
                          <br />
                          Units: {String(point.data.y)}
                        </div>
                      )}
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              icon={<DismissRegular />}
            >
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SkuChartsModal;
